import { Point } from '@models/connector';

const DEFINITION_TAGS = new Set(['mask', 'clippath', 'defs', 'filter', 'marker']);
const DEFS_CHILD_TAGS = new Set(['mask', 'clippath', 'filter', 'marker']);
const URL_REF_ATTRS = ['fill', 'clip-path', 'mask', 'filter', 'stroke', 'marker-start', 'marker-end', 'marker-mid'];

function stripTextNodes(el: Element): void {
    for (let i = el.childNodes.length - 1; i >= 0; i--) {
        const node = el.childNodes[i];
        if (node.nodeType === Node.TEXT_NODE) {
            el.removeChild(node);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            stripTextNodes(node as Element);
        }
    }
}

export function appendIconChildren(
    sourceG: Element,
    targetG: SVGElement,
    getNextId: () => string,
    defsTarget?: Element
): void {
    const clones = Array.from(sourceG.children).map(c => c.cloneNode(true) as SVGElement);

    const idMap = new Map<string, string>();
    for (const clone of clones) {
        for (const el of [clone as Element, ...Array.from(clone.querySelectorAll('[id]'))]) {
            const id = el.getAttribute('id');
            if (id && !idMap.has(id)) {
                idMap.set(id, getNextId());
            }
        }
    }

    const remapIds = (el: Element) => {
        const ownId = el.getAttribute('id');
        if (ownId && idMap.has(ownId)) {
            el.setAttribute('id', idMap.get(ownId)!);
        }
        for (const attr of URL_REF_ATTRS) {
            const val = el.getAttribute(attr);
            if (val?.includes('url(#')) {
                el.setAttribute(
                    attr,
                    val.replace(/url\(#([^)]+)\)/g, (_, ref) =>
                        idMap.has(ref) ? `url(#${idMap.get(ref)})` : `url(#${ref})`
                    )
                );
            }
        }
    };

    for (const clone of clones) {
        for (const el of [clone as Element, ...Array.from(clone.querySelectorAll('*'))]) {
            remapIds(el);
        }

        const tag = clone.tagName.toLowerCase();

        if (tag === 'defs') {
            if (defsTarget) {
                while (clone.firstElementChild) {
                    const defChild = clone.firstElementChild;
                    stripTextNodes(defChild);
                    defsTarget.appendChild(defChild);
                }
            } else {
                targetG.appendChild(clone);
            }
            continue;
        }

        if (DEFS_CHILD_TAGS.has(tag)) {
            const dest = defsTarget ?? targetG;
            stripTextNodes(clone);
            dest.appendChild(clone);
            continue;
        }

        if (!clone.getAttribute('id')) {
            clone.setAttribute('id', getNextId());
        }

        for (const child of Array.from(clone.querySelectorAll('*'))) {
            if (!child.getAttribute('id') && !DEFINITION_TAGS.has(child.tagName.toLowerCase())) {
                child.setAttribute('id', getNextId());
            }
        }

        if (!DEFINITION_TAGS.has(tag)) {
            clone.setAttribute('fill', '#ffffff');
        }

        targetG.appendChild(clone);
    }
}

export function calculateClickPosition(point: Point) {
    return {
        curX: point.x,
        curY: point.y,
        ox: 0,
        oy: 0,
        x: 0,
        y: 0,
    };
}

export function transformPoint(x, y, m) {
    return {
        x: m.a * x + m.c * y + m.e,
        y: m.b * x + m.d * y + m.f,
    };
}

export function snapToGrid(value, canvas) {
    const unit = canvas.getBaseUnit();
    let stepSize = canvas.getSnappingStep();
    if (unit !== 'px') {
        stepSize *= canvas.getTypeMap()[unit];
    }
    value = Math.round(value / stepSize) * stepSize;
    return value;
}

export function checkChildNodes(element) {
    let elementsToMove = [];

    function traverse(node) {
        if (!node) return;
        let nodes = node?.children;
        if (nodes?.length > 0) {
            for (let i = 0; i < nodes.length; i++) {
                if (nodes[i].tagName == 'g') {
                    traverse(nodes[i]);
                } else {
                    if (nodes[i].getAttribute('pointPosition')) {
                        elementsToMove.push(nodes[i]);
                    }
                }
            }
        } else {
            if (node.getAttribute('pointPosition')) {
                elementsToMove.push(node);
            }
        }
    }

    traverse(element);
    return elementsToMove;
}

export function addPointHoverStyle(element) {
    element.addEventListener('mouseenter', () => {
        element.setAttribute('r', 6);
        element.setAttribute('fill', 'red');
        element.setAttribute('stroke', 'red');
    });

    element.addEventListener('mouseleave', () => {
        element.setAttribute('r', 4);
        element.setAttribute('fill', '#0FF');
        element.setAttribute('stroke', '#00F');
    });
}

export function deletePaths(element, canvas) {
    const svgRoot = canvas.getSvgRoot();
    const layer = svgRoot.getElementsByClassName('layer');
    const paths = layer[0].getElementsByTagName('path');
    const selectedId = element.id;
    const pathsToDelete = [];
    for (let i = 0; i < paths.length; i++) {
        const pathId = paths[i].id;
        if (pathId.includes(selectedId) && pathId.startsWith('connect-')) {
            pathsToDelete.push(paths[i]);
        }
    }
    pathsToDelete.forEach(path => {
        canvas.addCommandToHistory(
            new canvas.history.RemoveElementCommand(path, path.nextSibling, layer[0], 'delete connector path')
        );
        path.remove();
    });
}
